import React, { useMemo, useState } from 'react';
// @ts-ignore
import styles from './PasswordField.module.scss';
import PasswordChecklist, { RuleNames } from 'react-password-checklist';
import { useEffectAsync } from '../../../../helpers';
import apiClient from '../../../../Services/ApiClient';
import useApiClient from '../../../../Hooks/useApiClient';

interface CheckListItemDefault {
	type: RuleNames;
	message: string;
}

interface CheckListItemBoolean extends CheckListItemDefault {
	value: boolean;
}

interface CheckListItemNumber extends CheckListItemDefault {
	value: number;
}

type CheckListItem = CheckListItemBoolean | CheckListItemNumber;

interface Props {
	name: string;
	isRequired?: boolean;
	placeholder?: string;
	value?: string;
	onChange?: (value: string) => void;
	checklist?: CheckListItem[];
	netteRules?: string;
	loadChecklist?: boolean;
}

const PasswordField: React.FC<Props> = props => {
	const {
		name,
		isRequired,
		value: propValue,
		onChange,
		placeholder,
		netteRules,
		loadChecklist = false,
	} = props;
	const [isHidden, setIsHidden] = useState(true);
	const [value, setValue] = useState<string>(propValue ?? '');
	const [checklist, setChecklist] = useState<CheckListItem[] | undefined>(
		props.checklist,
	);
	const { displayError } = useApiClient();

	const handleSetValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		// @ts-ignore
		const value = event.target.value;
		setValue(value);
		onChange?.(value);
	};

	const {
		rules,
		messages,
		minLength,
		maxLength,
	}: {
		rules: RuleNames[];
		messages: Record<RuleNames, string>;
		minLength?: number;
		maxLength?: number;
	} = useMemo(() => {
		const rules: RuleNames[] = [];
		const messages = {} as Record<RuleNames, string>;
		let minLength: number | undefined = undefined;
		let maxLength: number | undefined = undefined;

		checklist?.forEach(item => {
			if (
				(typeof item.value === 'boolean' && item.value) ||
				typeof item.value === 'number'
			) {
				rules.push(item.type);
				messages[item.type as RuleNames] = item.message;
			}
			if (typeof item.value === 'number' && item.type === 'minLength') {
				minLength = item.value;
			}
			if (typeof item.value === 'number' && item.type === 'maxLength') {
				maxLength = item.value;
			}
		});

		return { rules, messages, minLength, maxLength };
	}, [checklist]);

	useEffectAsync(async () => {
		if (loadChecklist) {
			try {
				const response = await apiClient.get('/user/password-validation');
				if (response.status === 200) {
					setChecklist(response.data);
				}
			} catch (e) {
				displayError(e);
			}
		}
	}, []);

	return (
		<>
			<div className={styles.wrapper}>
				<input
					type={isHidden ? 'password' : 'text'}
					name={name}
					required={isRequired}
					defaultValue={value}
					placeholder={placeholder}
					className={styles.input}
					onChange={handleSetValue}
					data-nette-rules={netteRules}
				/>
				<img
					src='/dist/patientV3/icons/eye-solid.svg'
					alt='eye'
					onClick={() => setIsHidden(prev => !prev)}
				/>
			</div>
			{checklist && value && (
				<PasswordChecklist
					rules={rules}
					className='checklist'
					value={value}
					minLength={minLength}
					maxLength={maxLength}
					messages={messages}
				/>
			)}
		</>
	);
};

export default PasswordField;
